import { useCallback, useRef } from 'react';

import { cn } from '../../lib/css/className/className';
import { useHStack, useVStack } from '../../hooks/useStack';
import { useScrollbarWidth } from '../../hooks/useScrollbarWidth/useScrollbarWidth';
import { useIsFullyScrolled } from '../../hooks/useIsFullyScrolled/useIsFullyScrolled';
import { VStack, HStack } from '../Stack';
import { Card } from '../Card';

import { OverlayContentProps } from './types';

import './OverlayContent@desktop.css';

const cls = cn('OverlayContent');

/**
 * Компонент для отображения контента в оверлеях – Modal, SidePanel и т.д.
 */
export default function OverlayContent(props: OverlayContentProps) {
    const {
        children,
        width,
        bottomButtons,
        bottomLeftControls,
        leftBar,
        leftBarWidth = '236',
        rightBar,
        rightBarWidth = '236',
        className,
    } = props;

    const centerStack = useVStack({ gap: '8', scrollable: true });
    const contentStack = useHStack({ gap: '12' });
    const footerStack = useHStack({
        gap: '8',
        alignItems: 'center',
        justifyContent: bottomLeftControls ? 'space-between' : 'flex-end',
    });
    const { scrollbarWidth, setRef: setScrollbarRef } = useScrollbarWidth();
    const { ref: scrollRef, onScroll, isFullyScrolled } = useIsFullyScrolled();
    const centerRef = useRef<HTMLDivElement | null>();

    const isShadowVisible = !isFullyScrolled;

    const setRef = useCallback(
        (node: HTMLDivElement | null) => {
            setScrollbarRef(node);
            scrollRef(node);
            centerRef.current = node;
        },
        [setScrollbarRef, scrollRef],
    );

    const mainContent = (
        <div
            ref={setRef}
            style={{
                width: width ? Number(width) : undefined,
                marginRight: scrollbarWidth ? -scrollbarWidth : undefined,
            }}
            className={cls('center', { noWidth: !width }, [centerStack])}
            onScroll={onScroll}
        >
            {children}
        </div>
    );

    return (
        <VStack
            className={cls(
                {
                    withBottomButtons: Boolean(bottomButtons),
                },
                [className],
            )}
            // В Кейсе когда width не задан - растягиваем по контейнеру
            // необходимо пока есть модалки, у которых ширина задана через css (пока идет миграция)
            {...(width ? { inline: true } : {})}
        >
            <Card color="gray-3" className={cls('content', [contentStack])}>
                {leftBar && <HStack className={cls('leftbar', { width: leftBarWidth })}>{leftBar}</HStack>}
                {mainContent}
                {rightBar && <div className={cls('rightbar', { width: rightBarWidth })}>{rightBar}</div>}
            </Card>
            {(bottomButtons || bottomLeftControls) && (
                <Card
                    color="gray-4"
                    padding="12"
                    className={cls('buttons', { withShadow: isShadowVisible }, [footerStack])}
                >
                    {bottomLeftControls && <HStack gap="8">{bottomLeftControls}</HStack>}
                    {bottomButtons && (
                        <HStack gap="8" width={bottomLeftControls ? undefined : 'max'} justifyContent="flex-end">
                            {bottomButtons}
                        </HStack>
                    )}
                </Card>
            )}
        </VStack>
    );
}
